/**
 * The consultant's dashboard_widget card (contract v1, 2026-09-26; applied from 2026-09-28): a trend of 1 to 4 of the
 * org's tags over a range, added to a board the org has, or to a new board made for it. The person's browser adds it
 * as the Dashboards page adds a widget, through the same dashboards routes, as them (dashboards.write): a board the org
 * has is read as the platform holds it, given the widget below its others, and saved whole (PATCH
 * /v1/iot/dashboards/{id}, with the stamp it was read at, so an edit saved in between is never replaced: addToBoard);
 * a new board is made with the widget on it (POST /v1/iot/dashboards, on an id the page gives it). Pure, with the calls injected: lib/api/copilot-imports runs them, and copilot-proposals checks the card
 * (planApply) and records what the approval came to (approveProposal).
 *
 * A board's widget plots a tag by its name, as the board's own picker binds it (the "Live tags" alias), and the board
 * finds the org's tag by that name, lower-cased (lib/dashboard/use-live-tag-data). The card names its tags by id, so
 * the page binds each by the name the org's tag list gives it, and refuses a tag it could not plot as the card
 * proposes: one the org does not have, or one whose name another of the org's tags shares.
 *
 * v2 (B6, 2026-10-19), a board of up to 12 widgets with one approval, is copilot-board.ts: it shares this file's calls,
 * its tags' binding by name and where a widget is added below a board's others.
 */
import type { Refusal } from "./copilot";
import { readSlot, type DashboardConfig, type WidgetInstance } from "@/lib/dashboard/types";
import type { RangeToken } from "@/lib/dashboard/use-live-tag-data";

/** Where boards show: the Dashboards page, and each board's own page. */
export const DASHBOARDS_URL = "/app/dashboards";

export function dashboardUrl(id: string): string {
  return `${DASHBOARDS_URL}/${id}`;
}

/**
 * The ranges a card's trend takes: the lookbacks a board's widget pins (RangeToken), but "all" and the hour windows
 * (the copilot's cards name 24h, 7d, 30d or 1y: platform-core twin/copilot_blocks.go).
 */
export type TrendRange = Exclude<RangeToken, "all" | "1h" | "6h">;

export const TREND_RANGES: readonly TrendRange[] = ["24h", "7d", "30d", "1y"];

/** The trend a card adds: its title, the org's tags it plots (by id, 1 to 4), and its range. */
export interface TrendPlan {
  title: string;
  tagIds: string[];
  range: TrendRange;
}

/**
 * How the page applies a dashboard_widget card (a copilot-proposals ApplyPlan): the board, one the org has (by id) or
 * a new one (by name), and the trend. `url` is where it shows: the board's page, or the Dashboards page for a new
 * board, whose own page is known once it is made (the recorded result links to the board either way).
 */
export interface WidgetPlan {
  kind: "dashboard_widget";
  board: { id: string } | { name: string };
  widget: TrendPlan;
  url: string;
}

/**
 * A widget the page does not add, in its own words, decided before it writes anything (a tag it cannot plot as the
 * card proposes). The approval records it as the failure and looks for nothing, since nothing was sent.
 */
export class WidgetRefused extends Error {
  readonly refusal: Refusal;

  constructor(text: string) {
    super(text);
    this.name = "WidgetRefused";
    this.refusal = { text, code: null };
  }
}

/** One of the org's tags, as a widget binds it: its id and its name. */
export interface OrgTag {
  id: string;
  name: string;
}

/**
 * The platform refused a save because the board changed after it was read (409 board_changed): someone else saved it
 * between the read and the write. Nothing was written. Thrown by BoardCalls.save; addToBoard reads again and retries,
 * and tells the person when the board keeps moving.
 */
export class BoardChanged extends Error {
  constructor() {
    super("The board changed after it was read.");
    this.name = "BoardChanged";
  }
}

/** The calls adding a widget makes, as the person: injected, so what they do is tested without a platform. */
export interface BoardCalls {
  /** The org's tags (GET /v1/iot/tags, every page). */
  tags: () => Promise<OrgTag[]>;
  /** A board of the org as the platform holds it (GET /v1/iot/dashboards/{id}); its updatedAt is the platform's stamp. */
  board: (id: string) => Promise<DashboardConfig>;
  /** Every board of the org (GET /v1/iot/dashboards, every page). */
  boards: () => Promise<DashboardConfig[]>;
  /** Makes a board with its widgets (POST /v1/iot/dashboards). */
  create: (board: DashboardConfig) => Promise<void>;
  /**
   * Saves a board with its widgets (PATCH /v1/iot/dashboards/{id}). `expectedUpdatedAt`, the stamp the board was read at,
   * is sent as the precondition (expected_updated_at): the platform writes nothing and this throws BoardChanged when
   * the board moved on. Left out, the save is unconditional.
   */
  save: (board: DashboardConfig, expectedUpdatedAt?: string) => Promise<void>;
  /** A fresh id (a UUID): a new board's, and a widget's. */
  newId: () => string;
}

const TAG_NOT_FOUND = "A tag of the widget was not found in this org.";

/** The card's tags as the widget binds them: their names, in the card's order, or why they cannot be bound. */
export type TagBinding = { names: string[] } | { refusal: string };

/**
 * The names the widget binds the card's tags by, or why it cannot bind them: a tag the org does not have (or has
 * without a name), or a name another of the org's tags shares, which the board would plot either of.
 */
export function trendTagNames(tagIds: readonly string[], tags: readonly OrgTag[]): TagBinding {
  const named = new Map<string, number>();
  for (const t of tags) {
    if (t.name) named.set(t.name.toLowerCase(), (named.get(t.name.toLowerCase()) ?? 0) + 1);
  }
  const names: string[] = [];
  for (const id of tagIds) {
    const tag = tags.find((t) => t.id.toLowerCase() === id.toLowerCase());
    if (!tag?.name) return { refusal: TAG_NOT_FOUND };
    if ((named.get(tag.name.toLowerCase()) ?? 0) > 1) {
      return {
        refusal: `More than one of this org's tags is named "${tag.name}", so the widget could not tell which one to plot.`,
      };
    }
    names.push(tag.name);
  }
  return { names };
}

/** The Time-series widget's default size (widget-registry), which the Dashboards page gives a widget it adds. */
const TREND_SIZE = { w: 6, h: 4 };
/** The alias the board's picker lists the org's tags under, by name (EntityAliasPicker). */
export const LIVE_TAGS = "Live tags";

/** The first row below every widget on a board: where the Dashboards page puts a widget it adds (at x 0). */
export function nextRow(widgets: readonly WidgetInstance[]): number {
  let row = 0;
  for (const w of widgets) {
    // A widget saved without a layout takes no rows.
    const bottom = Number(w.layout?.y) + Number(w.layout?.h);
    if (Number.isFinite(bottom) && bottom > row) row = bottom;
  }
  return row;
}

/**
 * The trend as the board's editor makes it: a Time-series widget with the card's title, its Series bound to the org's
 * live tags by name, and the card's range pinned (config.range, where the widget's range picker saves it).
 */
function trendWidget(id: string, trend: TrendPlan, names: readonly string[], row: number): WidgetInstance {
  return {
    id,
    type: "timeseries",
    title: trend.title,
    layout: { x: 0, y: row, w: TREND_SIZE.w, h: TREND_SIZE.h },
    dataSource: { kind: "tag", alias: LIVE_TAGS, slots: { series: [...names] } },
    config: { range: trend.range },
  };
}

/** A widget that is the card's trend: a Time-series of the same title, tags and range. */
function sameTrend(w: WidgetInstance, trend: TrendPlan, names: readonly string[]): boolean {
  if (w.type !== "timeseries" || (w.title ?? "").trim() !== trend.title) return false;
  if (w.config?.range !== trend.range) return false;
  const bound = new Set(readSlot(w.dataSource, "series"));
  return bound.size === names.length && names.every((n) => bound.has(n));
}

/** How many times a save refused as stale is read again and tried once more, before the person is told. */
export const BOARD_SAVE_RETRIES = 2;

/** What the person is told when the board kept changing under the approval: nothing was added, and they may approve again. */
export const BOARD_KEPT_CHANGING =
  "The board changed while you approved, so nothing was added. Approve again to add it to the board as it is now.";

/**
 * Adds widgets below the widgets of a board the org has: reads the board as the platform holds it, lets `widgetsFor`
 * make the widgets for the rows below it, and saves the whole board with the stamp it was read at, so an edit saved in
 * between is refused rather than replaced. A refusal (BoardChanged) reads the board again and adds to that fresh copy,
 * at most BOARD_SAVE_RETRIES times; then it throws WidgetRefused (nothing was written) saying the board changed.
 * Gives the board's id.
 */
export async function addToBoard(
  id: string,
  calls: BoardCalls,
  widgetsFor: (board: DashboardConfig) => WidgetInstance[],
): Promise<{ id: string }> {
  for (let tried = 0; ; tried++) {
    const board = await calls.board(id);
    const widgets = widgetsFor(board);
    try {
      await calls.save(
        { ...board, widgets: [...board.widgets, ...widgets], updatedAt: new Date().toISOString() },
        board.updatedAt || undefined,
      );
      return { id: board.id };
    } catch (err) {
      if (!(err instanceof BoardChanged)) throw err;
      if (tried >= BOARD_SAVE_RETRIES) throw new WidgetRefused(BOARD_KEPT_CHANGING);
    }
  }
}

/**
 * Adds the card's trend: below the widgets of the board it names (read as the platform holds it, then saved whole),
 * or on a new board made with it. Nothing is written when a tag cannot be bound (WidgetRefused). Gives the board's
 * id, which the card's result links to.
 */
export async function addTrendWidget(plan: WidgetPlan, calls: BoardCalls): Promise<{ id: string }> {
  const bound = trendTagNames(plan.widget.tagIds, await calls.tags());
  if ("refusal" in bound) throw new WidgetRefused(bound.refusal);
  if ("name" in plan.board) {
    const id = calls.newId();
    const widget = trendWidget(`w-timeseries-${calls.newId()}`, plan.widget, bound.names, 0);
    await calls.create({
      id,
      name: plan.board.name,
      widgets: [widget],
      source: "my",
      owner: "You",
      updatedAt: new Date().toISOString(),
    });
    return { id };
  }
  return addToBoard(plan.board.id, calls, (board) => [
    trendWidget(`w-timeseries-${calls.newId()}`, plan.widget, bound.names, nextRow(board.widgets)),
  ]);
}

/**
 * The card's trend where it was added already (by another tab, or by an approval whose answer was lost): its board's
 * id, or null. On a board the org has, that board holds it; on a new board, a board of the card's name holds it.
 */
export async function findTrendWidget(plan: WidgetPlan, calls: BoardCalls): Promise<string | null> {
  const bound = trendTagNames(plan.widget.tagIds, await calls.tags());
  // A tag the page cannot bind: it never added this trend.
  if ("refusal" in bound) return null;
  const holds = (b: DashboardConfig) => b.widgets.some((w) => sameTrend(w, plan.widget, bound.names));
  if ("id" in plan.board) {
    const board = await calls.board(plan.board.id);
    return holds(board) ? board.id : null;
  }
  const name = plan.board.name;
  return (await calls.boards()).find((b) => b.name.trim() === name && holds(b))?.id ?? null;
}
